import { atom } from 'jotai';
import { atomWithMutation, atomWithQuery } from 'jotai-tanstack-query';
import { isAxiosError } from 'axios';

import { instance } from '@/interfaces';
import { devicesAtom, getDevices } from '@/interfaces/devices';
import { userInfoAtom } from '@/interfaces/member';

import type { APIResponse } from '@/@types/types';
import type { ProjectSensorData } from '@/@types/project';
import type { Getter } from 'jotai';

export const selectedProjectIdAtom = atom('');

export const projectSensorsWithDataAtom = atomWithQuery((get: Getter) => ({
  queryKey: ['PROJECT_SENSORS', get(selectedProjectIdAtom)],
  queryFn: async ({ queryKey: [, projectId] }) => {
    let { data: devices } = get(devicesAtom);
    if (devices === undefined) {
      const { data: userInfo } = get(userInfoAtom);
      devices = await getDevices(userInfo.site[0].id);
    }

    const projectSensors = await getProjectSensors(projectId);
    const sensorIds = projectSensors.map((sensor) => sensor.id);
    const result = devices.filter((device) => sensorIds.includes(device.id));
    return result;
  },
}));
export async function getProjectSensors(projectId: string) {
  try {
    const { data } = await instance.get<APIResponse<ProjectSensorData[]>>(`/project/${projectId}/sensors`);
    return data.data;
  } catch (err) {
    if (isAxiosError(err)) {
      console.error('[getProjectSensors] API error code:', err.response.data.errorCode);
    } else {
      console.error('[getProjectSensors] Unknown error', err);
    }
    return [];
  }
}

export const deleteProjectAtom = atomWithMutation(() => ({
  mutationFn: async (projectId: string) => {
    const isSuccess = await deleteProject(projectId);
    return isSuccess;
  },
}));
async function deleteProject(projectId: string) {
  try {
    const { data } = await instance.delete<APIResponse>(`/project/${projectId}`);
    if (data.errorCode !== 'OK') {
      console.error('[deleteProject] API error code:', data.errorCode);
      return false;
    }
    return true;
  } catch (err) {
    if (isAxiosError<APIResponse>(err)) {
      console.error('[deleteProject] API error code:', err.response.data.errorCode);
    } else {
      console.error('[deleteProject] Unknown error', err);
    }

    return false;
  }
}

type AddDeviceToProjectParams = { projectId: string; sensorIds: string[] };
export const addDeviceToProjectAtom = atomWithMutation(() => ({
  mutationFn: async ({ projectId, sensorIds }: AddDeviceToProjectParams) => {
    const isSuccess = await addDeviceToProject(projectId, sensorIds);
    return isSuccess;
  },
}));
async function addDeviceToProject(projectId: string, sensorIds: string[]) {
  try {
    const { data } = await instance.post<APIResponse>(`/project/${projectId}/sensor`, { sensorIds });
    if (data.errorCode !== 'OK') {
      console.error('[addDeviceToProject] API error code:', data.errorCode);
      return false;
    }
    return true;
  } catch (err) {
    if (isAxiosError<APIResponse>(err)) {
      console.error('[addDeviceToProject] API error code:', err.response.data.errorCode);
    } else {
      console.error('[addDeviceToProject] Unknown error', err);
    }

    return false;
  }
}

type PatchProjectAtomParams = { projectId: string; projectName: string; priority: boolean };
export const patchProjectAtom = atomWithMutation(() => ({
  mutationFn: async ({ projectId, projectName, priority }: PatchProjectAtomParams) => {
    const isSuccess = await patchProject(projectId, projectName, priority);
    return isSuccess;
  },
}));
export async function patchProject(projectId: string, projectName: string, priority: boolean) {
  try {
    const { data } = await instance.patch<APIResponse>(`/project/${projectId}`, { projectName, priority });
    if (data.errorCode !== 'OK') {
      console.error('[patchProject] API error code:', data.errorCode);
      return false;
    }
    return true;
  } catch (err) {
    if (isAxiosError(err)) {
      if (err.response) {
        console.error('[patchProject] API error code:', err.response.data.errorCode);
      } else {
        console.error('[patchProject] Axios error message:', err.message);
      }
    } else {
      console.error('[patchProject] Unknown error', err);
    }
    return false;
  }
}
